import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getSupabase } from "@/integrations/supabase/lazy";
import { ArrowRight, Calendar, Heart, Dumbbell, Sparkles, Music, Users, HandHeart, Loader2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";

// Static image imports for known items
import yogaImage from "@/assets/yoga-metime.webp";
import gesundheitssportImage from "@/assets/gesundheitssport-1.webp";
import jazzLoungeImage from "@/assets/jazz-lounge.webp";
import adventsmarktImage from "@/assets/adventsmarkt-444.webp.asset.json";

// Map icon names to components
const iconMap: Record<string, LucideIcon> = {
  Calendar, Heart, Dumbbell, Sparkles, Music, Users, HandHeart,
};

// Map image keys to imported assets
const imageMap: Record<string, string> = {
  "adventsmarkt": adventsmarktImage.url,
  "jazz-lounge": jazzLoungeImage,
  "yoga": yogaImage,
  "gesundheitssport": gesundheitssportImage,
};

interface NewsItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  date_label: string;
  link: string;
  icon_name: string;
  image_url: string | null;
  featured: boolean;
  event_id: string | null;
}

export function NewsSection() {
  const { data: newsItems = [], isLoading } = useQuery({
    queryKey: ["public-news-items"],
    queryFn: async () => {
      const supabase = await getSupabase();
      // Fetch active news items
      const { data: items, error } = await supabase
        .from("news_items")
        .select("id, title, subtitle, description, date_label, link, icon_name, image_url, featured, event_id")
        .eq("is_active", true)
        .order("position", { ascending: true });
      if (error) throw error;

      if (!items || items.length === 0) return [];

      // Get event_ids to check their status
      const eventIds = items
        .map((i) => i.event_id)
        .filter((id): id is string => !!id);

      if (eventIds.length === 0) return items as NewsItem[];

      const { data: events } = await supabase
        .from("events")
        .select("id, status")
        .in("id", eventIds);

      const eventStatusMap = new Map(
        (events || []).map((e) => [e.id, e.status])
      );

      // Filter out items whose linked event is abgeschlossen or abgesagt
      return (items as NewsItem[]).filter((item) => {
        if (!item.event_id) return true;
        const status = eventStatusMap.get(item.event_id);
        return status !== "abgeschlossen" && status !== "abgesagt";
      });
    },
    staleTime: 5 * 60 * 1000, // cache for 5 minutes
  });

  // Replace the former crowdfunding teaser on the homepage only.
  const displayedNewsItems = newsItems.map((item): NewsItem => {
    if (item.link !== "/crowdfunding" && !/99funken/i.test(`${item.title} ${item.link}`)) return item;
    return {
      ...item,
      title: "Kultmans Adventsmarkt",
      subtitle: "Skandinavische Weihnacht im HOF 1 · Eintritt frei",
      description: "Kunsthandwerk, winterliche Leckereien, Musik und Kinderprogramm im Alten Stemke Werk & HOF 1 in Döbeln. Samstag 13:00–22:00 Uhr, Sonntag 13:00–17:00 Uhr. Sonntag: Gemütlicher Ausklang ab 17:00 Uhr.",
      date_label: "28. & 29. November 2026",
      link: "/adventsmarkt",
      icon_name: "Calendar",
      image_url: "adventsmarkt",
    };
  }).filter((item, index, items) => item.link !== "/adventsmarkt" || items.findIndex((candidate) => candidate.link === "/adventsmarkt") === index);

  const getIcon = (name: string) => iconMap[name] || Calendar;
  const getImage = (imageUrl: string | null) => {
    if (!imageUrl) return null;
    // Check static map first, then treat as URL
    return imageMap[imageUrl] || imageUrl;
  };

  return (
    <section className="py-12 md:py-24 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8 md:mb-16">
          <h2 className="font-display text-2xl sm:text-3xl md:text-5xl font-bold text-foreground mb-4">
            Aktuelles
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Bleibe informiert über Neuigkeiten, Events und Projekte des Kultmans e.V.
          </p>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : newsItems.length === 0 ? (
          <p className="text-center text-muted-foreground py-12">
            Aktuell keine Neuigkeiten vorhanden.
          </p>
        ) : (
          <>
            {/* News Grid - 3 columns on desktop */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {displayedNewsItems.map((item, index) => {
                const IconComp = getIcon(item.icon_name);
                const image = getImage(item.image_url);

                return (
                  <Card
                    key={item.id}
                    className={`hover-lift border-border bg-card overflow-hidden group ${
                      item.featured ? "ring-2 ring-primary/50" : ""
                    } ${image ? "flex flex-col" : ""}`}
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    {image && (
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={image}
                          alt={item.title}
                          width={800}
                          height={384}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-card/80 to-transparent" />
                      </div>
                    )}
                    <CardHeader className={image ? "pb-2 pt-4" : "pb-4"}>
                      {!image && (
                        <div className="flex items-center justify-between mb-4">
                          <div
                            className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                              item.featured
                                ? "bg-primary text-primary-foreground"
                                : "bg-secondary text-muted-foreground"
                            }`}
                          >
                            <IconComp size={24} />
                          </div>
                          {item.featured && (
                            <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary">
                              Neu
                            </span>
                          )}
                        </div>
                      )}
                      {image && item.featured && (
                        <span className="text-xs font-medium px-2 py-1 rounded-full bg-primary/10 text-primary w-fit mb-2">
                          Neu
                        </span>
                      )}
                      <p className="text-sm text-muted-foreground">{item.date_label}</p>
                      <h3 className="font-display text-xl font-semibold text-foreground group-hover:text-primary transition-colors">
                        {item.title}
                      </h3>
                      <p className="text-primary font-medium text-sm">{item.subtitle}</p>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <p className="text-muted-foreground text-sm mb-4 flex-1">
                        {item.description}
                      </p>
                      <Link
                        to={item.link}
                        className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:gap-3 transition-all"
                      >
                        Mehr erfahren
                        <ArrowRight size={16} />
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </>
        )}

        {/* CTA */}
        <div className="text-center">
          <Button variant="outline" size="lg" asChild>
            <Link to="/veranstaltungen">
              Alle Veranstaltungen ansehen
              <ArrowRight size={18} />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}
